import { css } from '@emotion/react';
import { faChartSimple } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useMemo,
} from 'react';
import { Accordion } from '~/_shared/baseComponents/accordion';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { OutlinePanelComponent } from '~/_shared/baseComponents/outlinePanel/outlinePanel.component';
import { OutlinePanelOptions } from '~/_shared/baseComponents/outlinePanel/outlinePanel.constants';
import { hiddenStyle } from '~/_shared/components/accountSettings/modalDialogs/shared.styles';
import {
  EmptyStateComponent, EmptyStateSize,
} from '~/_shared/components/emptyState/emptyState.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { TabsComponent } from '~/_shared/components/tabs/tabs.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { MIN_NUMERIC_AND_EMPTY_PERCENTAGE } from '~/_shared/utils/spreadsheet/useIsNumericColumnCheck.helpers';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { ContinueButton } from '~/map/layered/createLayeredMapModal/ContinueButton';
import { DEFAULT_METRIC_VALUE_WEIGHT } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.helpers';
import {
  type GlobalMetricValue, type MetricValue,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { DataWeightsComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/dataWeights.component';
import { DataWeightsItemComponent } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/dataWeightsItem.component';
import {
  SELECTABLE_METRIC_LINE_SIZE, SelectableMetricsListComponent,
} from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/selectableMetricsList.component';

const NUMBER_OF_VISIBLE_METRICS = 4.25;

const continueRowStyle = ({ showFilter }: {showFilter: boolean}) => css({
  display: 'flex',
  justifyContent: showFilter ? 'space-between' : 'flex-end',
  marginTop: 20,
});

const accordionStyle = css({
  padding: 0,
  '&:first-of-type': {
    borderRadius: '4px 4px 0 0',
  },
});

const accordionItemStyle = css({
  '&:first-of-type': {
    '& > div': {
      borderRadius: '4px 4px 0 0',
    },
  },
  '&:last-of-type': {
    borderRadius: '0 0 4px 4px',
    '& > div:last-of-type': {
      borderRadius: '0 0 4px 4px',
    },
  },
});

const defaultCursorStyle = css({
  cursor: 'default',
});

const descriptionStyle = css(defaultCursorStyle, {
  fontSize: 16,
  marginBottom: 16,
});

const selectVariablesStyle = css(defaultCursorStyle, {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
});

const tabStyle = css({
  '&:first-of-type': {
    borderRadius: '4px 0 0',
  },
  '&:last-of-type': {
    borderRadius: '0 4px 0 0 ',
  },
});

const metricsContainerStyle = css({
  height: SELECTABLE_METRIC_LINE_SIZE * NUMBER_OF_VISIBLE_METRICS,
  width: '100%',
});

const emptyStateStyles = css({
  display: 'flex',
  alignContent: 'center',
  justifyContent: 'center',
  height: '100%',
  flexWrap: 'wrap',
});

const globalMetricsWrapperStyle = css({
  padding: 16,
});

export type VariablesStepComponentProps = Readonly<{
  applyFilterOnData: boolean;
  onContinue: () => void;
  isLoadingDataMetrics: boolean;
  dataMetricsValues: ReadonlyArray<MetricValue>;
  demographicsValues: ReadonlyArray<MetricValue>;
  globalMetrics?: ReadonlyArray<GlobalMetricValue>;
  minWeight: number;
  maxWeight: number;
  onChangeDataMetrics: (dataMetricsValues: ReadonlyArray<MetricValue>) => void;
  onChangeDemographics: (demographicsValues: ReadonlyArray<MetricValue>) => void;
  onChangeApplyFilterOnData?: (flag: boolean) => void;
  onChangeGlobalMetrics?: (metrics: ReadonlyArray<GlobalMetricValue>) => void;
}>;

export const VariablesStepComponent: FC<VariablesStepComponentProps> = ({
  applyFilterOnData,
  onContinue,
  dataMetricsValues,
  demographicsValues,
  isLoadingDataMetrics,
  onChangeDataMetrics,
  onChangeDemographics,
  onChangeApplyFilterOnData,
  globalMetrics,
  onChangeGlobalMetrics,
  minWeight,
  maxWeight,
}: VariablesStepComponentProps) => {
  const [t] = useTranslation();

  const selectableDataMetricsValues = useMemo(() => dataMetricsValues.map(dataMetric => ({
    id: dataMetric.id,
    groupId: dataMetric.groupId,
    label: dataMetric.name,
    checked: dataMetric.checked,
    disabled: !dataMetric.groupId && (isNullsy(dataMetric.numericPercent) || dataMetric.numericPercent < MIN_NUMERIC_AND_EMPTY_PERCENTAGE),
    numericPercent: dataMetric.numericPercent,
  })), [dataMetricsValues]);

  const selectableDemographicsValues = useMemo(() => demographicsValues.map(demographicsValue => ({
    id: demographicsValue.id,
    label: demographicsValue.name,
    checked: demographicsValue.checked,
  })), [demographicsValues]);

  const totalSelectedCount = useMemo(() => {
    const dataCount = dataMetricsValues.filter(m => m.checked).length;
    const demoCount = demographicsValues.filter(m => m.checked).length;
    return dataCount + demoCount;
  }, [dataMetricsValues, demographicsValues]);

  const onChangeDataMetric = useCallback(
    (id: { metricId: string; groupIds?: ReadonlyArray<string> }, checked: boolean) => {
      const selectedGroupIds = new Set(id.groupIds ?? []);

      const newMetricValues = dataMetricsValues.map(dataMetricsValue => {
        if (dataMetricsValue.id === id.metricId) {
          // Group update: update multiple groupIds for the same column
          if (id.groupIds) {
            if (dataMetricsValue.groupId) {
              const isInGroupIds = selectedGroupIds.has(dataMetricsValue.groupId);

              return {
                ...dataMetricsValue,
                checked: isInGroupIds,
                weight: isInGroupIds ? dataMetricsValue.weight : DEFAULT_METRIC_VALUE_WEIGHT,
              };
            }
          }
          // Single metric update
          else {
            if (isNullsy(dataMetricsValue.groupId)) {
              return {
                ...dataMetricsValue,
                checked,
                weight: checked ? dataMetricsValue.weight : DEFAULT_METRIC_VALUE_WEIGHT,
              };
            }
          }
        }

        return dataMetricsValue;
      });

      onChangeDataMetrics(newMetricValues);
    }, [dataMetricsValues, onChangeDataMetrics]);

  const onChangeDemographicsValue = useCallback((id: { metricId: string }, checked: boolean) => {
    const newMetricValues = demographicsValues.map(demographicsValue => {
      if (demographicsValue.id === id.metricId) {
        return {
          ...demographicsValue,
          checked,
          weight: checked ? demographicsValue.weight : DEFAULT_METRIC_VALUE_WEIGHT,
        };
      }
      else {
        return demographicsValue;
      }
    });

    onChangeDemographics(newMetricValues);
  }, [demographicsValues, onChangeDemographics]);

  const onChangeGlobalMetricValue = useCallback(({ id, weight }: {id: string; weight: number }) => {
    if (!globalMetrics || !onChangeGlobalMetrics) {
      return;
    }

    const newGlobalMetricsValues = globalMetrics.map(globalMetric => {
      if (globalMetric.id === id) {
        return {
          ...globalMetric,
          weight,
        };
      }

      return globalMetric;
    });

    onChangeGlobalMetrics(newGlobalMetricsValues);
  }, [globalMetrics, onChangeGlobalMetrics]);

  const canContinue = useMemo(() => {
    return dataMetricsValues.some(dataMetric => dataMetric.checked) ||
      demographicsValues.some(demographic => demographic.checked);
  }, [dataMetricsValues, demographicsValues]);

  const onSubmit = useCallback(() => {
    onContinue();
  }, [onContinue]);

  const showFilter = !!onChangeApplyFilterOnData;

  return (
    <>
      <div css={descriptionStyle}>
        {t('boundaryAI.creationVariables.description')}
      </div>
      <div css={selectVariablesStyle}>
        {t('boundaryAI.creationVariables')}
        <OutlinePanelComponent
          borderRadius={4}
          outlines={[OutlinePanelOptions.Right, OutlinePanelOptions.Bottom, OutlinePanelOptions.Left]}
        >
          <Accordion>
            <Accordion.Item
              css={accordionItemStyle}
              open
              isLocked
            >
              <Accordion.ItemHeading css={hiddenStyle} />
              <Accordion.Panel css={accordionStyle}>
                <TabsComponent
                  data={[{
                    header: t('Data Metrics').toUpperCase(),
                    index: 0,
                    child: (
                      <div css={metricsContainerStyle}>
                        {!isLoadingDataMetrics && selectableDataMetricsValues.length ? (
                          <SelectableMetricsListComponent
                            metrics={selectableDataMetricsValues}
                            onChange={onChangeDataMetric}
                            totalSelectedCount={totalSelectedCount}
                          />
                        ) : (
                          <div css={emptyStateStyles}>
                            {isLoadingDataMetrics ? (
                              <OverlayLoaderComponent />
                            ) : (
                              <EmptyStateComponent
                                css={{ padding: 10 }}
                                icon={faChartSimple}
                                title={t('No Data Columns Available')}
                                size={EmptyStateSize.Small}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ),
                  }, {
                    header: t('Demographic Census Data').toUpperCase(),
                    index: 1,
                    child: (
                      <div css={metricsContainerStyle}>
                        {selectableDemographicsValues.length > 0 ? (
                          <SelectableMetricsListComponent
                            metrics={selectableDemographicsValues}
                            onChange={onChangeDemographicsValue}
                            totalSelectedCount={totalSelectedCount}
                          />
                        ) : (
                          <div css={emptyStateStyles}>
                            <EmptyStateComponent
                              css={{ padding: 10 }}
                              icon={faChartSimple}
                              title={t('No demographics data available for selected building block type')}
                              size={EmptyStateSize.Small}
                            />
                          </div>
                        )}
                      </div>
                    ),
                  }]}
                  tabStyle={tabStyle}
                />
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item
              css={accordionItemStyle}
              open
              isLocked
            >
              <Accordion.ItemHeading>
                {t('boundaryAI.Summary data for territory optimizing with weights')}
              </Accordion.ItemHeading>
              <Accordion.Panel css={accordionStyle}>
                <DataWeightsComponent
                  dataMetricsValues={dataMetricsValues}
                  demographicsValues={demographicsValues}
                  onChangeDataMetrics={onChangeDataMetrics}
                  onChangeDemographics={onChangeDemographics}
                  minWeight={minWeight}
                  maxWeight={maxWeight}
                />
              </Accordion.Panel>
            </Accordion.Item>
            {globalMetrics && onChangeGlobalMetrics && (
              <Accordion.Item
                css={accordionItemStyle}
                open
                isLocked
              >
                <Accordion.ItemHeading>
                  {t('Shape regularity metric')}
                </Accordion.ItemHeading>
                <Accordion.Panel css={accordionStyle}>
                  <div css={globalMetricsWrapperStyle}>
                    {globalMetrics.map(metric => (
                      <DataWeightsItemComponent
                        key={metric.id}
                        onChange={onChangeGlobalMetricValue}
                        metric={metric}
                        minWeight={metric.minWeight}
                        maxWeight={metric.maxWeight}
                        step={5}
                        withDots
                      />
                    ))}
                  </div>
                </Accordion.Panel>
              </Accordion.Item>
            )}
          </Accordion>
        </OutlinePanelComponent>
      </div>
      <div css={continueRowStyle({ showFilter })}>
        {showFilter && (
          <div css={css({ display: 'flex', gap: 10, alignItems: 'center', width: 350 })}>
            <CheckboxComponent
              isChecked={applyFilterOnData}
              checkedSetter={() => onChangeApplyFilterOnData(!applyFilterOnData)}
            />
            <span
              onClick={() => onChangeApplyFilterOnData(!applyFilterOnData)}
              css={css(defaultCursorStyle, { textTransform: 'uppercase' })}
            >
              {t('boundaryAI.Apply Current Filter On Data')}
            </span>
          </div>
        )}
        <ContinueButton
          label={t('Continue')}
          onSubmitClick={onSubmit}
          disabled={!canContinue}
        />
      </div>
    </>
  );
};
